import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppService } from '@App';
import { FeatureToggle } from '@Enums';
import { IConfigService } from '@Config';
import { CaseMapkey } from '@Models';
import { BaseService, FeatureManagerService, ObservableService } from '@Services';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { MonitoringService } from '../../monitor.service';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable()
export class CaseMapKeyService extends BaseService {
    readonly baseUrl: string;
    readonly publicApiBaseUrl: string;

    constructor(
        public appService: AppService,
        public httpClient: HttpClient,
        public configService: IConfigService,
        public observableService: ObservableService,
        private readonly featureManagerService: FeatureManagerService,
        private readonly monitorService: MonitoringService
    ) {
        super(appService, configService, httpClient);

        this.baseUrl = this.appService.getAPIBaseURL();
        this.publicApiBaseUrl = this.configService.getConfiguration().publicApiUrl;
    }

    getVirtualMapKeys(accountId: string, caseId: string) {
        const clientId = this.appService.getClientId('');
        return lastValueFrom(super.getData<CaseMapkey[]>(`${this.publicApiBaseUrl}client/${clientId}/account/${accountId}/case/${caseId}/mapkeys/virtual`, undefined, true));
    }

    getIntegrationAffectedMapKeys(accountId: string, caseId: string, caseIntegrationQueueId: string) {
        const clientId = this.appService.getClientId('');
        return lastValueFrom(super.getData<CaseMapkey[]>(`${this.publicApiBaseUrl}client/${clientId}/account/${accountId}/case/${caseId}/caseintegrationqueue/${caseIntegrationQueueId}/mapkeys`, undefined, true));
    }

    /**
     * After an integration completes the server pushes the changed and recalculated map keys in the message
     * ('ok': nothing more is needed). 'fetch-required' means they didn't fit, so pull them by queue id, or re-evaluate all
     * VMKs when targeted refresh is off or can't be used. Anything else ('error', or not stated) re-evaluates all VMKs.
     */
    async updateAndMergeIntegrationMapKeys(accountId: string, caseId: string, caseIntegrationQueueId?: string, dependentMapKeyStatus?: string) {
        if (dependentMapKeyStatus === 'ok') {
            return;
        }

        // 'error', or not stated (no targeted refresh for this message): same as before, re-evaluate everything.
        if (dependentMapKeyStatus !== 'fetch-required') {
            await this.updateAndMergeVirtualMapKeys(accountId, caseId);
            return;
        }

        const targetedRefreshEnabled = await firstValueFrom(this.featureManagerService.isEnabled(
            FeatureToggle.CaseIntegrationsTargetedVirtualMapkeyRefresh, this.appService.getClientId('')));
        if (!targetedRefreshEnabled) {
            await this.updateAndMergeVirtualMapKeys(accountId, caseId);
            return;
        }

        if (!caseIntegrationQueueId || caseIntegrationQueueId === EMPTY_GUID) {
            console.warn(`Integration completed without a queue id for case ${caseId}; re-evaluating all VMKs`);
            this.monitorService.logEvent('Integration complete without queue id', { caseId });
            await this.updateAndMergeVirtualMapKeys(accountId, caseId);
            return;
        }

        let affectedMapkeys: CaseMapkey[];
        try {
            affectedMapkeys = await this.getIntegrationAffectedMapKeys(accountId, caseId, caseIntegrationQueueId);
        } catch {
            await this.updateAndMergeVirtualMapKeys(accountId, caseId);
            return;
        }

        if (affectedMapkeys != null && affectedMapkeys.length > 0) {
            this.observableService.triggerMergeMapKeys.next(affectedMapkeys);
        }
    }

    async updateAndMergeVirtualMapKeys(accountId: string, caseId: string) {
        const updatedMapkeys = await this.getVirtualMapKeys(accountId, caseId);

        if (updatedMapkeys != null && updatedMapkeys.length > 0) {
            this.observableService.triggerMergeMapKeys.next(updatedMapkeys);
        }
    }
}
